/**
 * optionsChainNormalizer.ts — MER Algo Footprints v7.1B — Phase 6
 *
 * Normalizes raw option contracts into a consistent, derived form.
 * Pure function — no side effects, no external calls.
 *
 * Placement: src/domains/market/engines/gamma/optionsChainNormalizer.ts
 */

import type { RawOptionContract, NormalizedOptionContract } from './gammaTypes';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Strike is considered ATM if within ±0.5% of spot. */
const ATM_BAND_PCT = 0.5;

/** Strike is considered near-money if within ±5% of spot. */
const NEAR_MONEY_PCT = 5.0;

// ─── Date helpers ─────────────────────────────────────────────────────────────

/**
 * Computes calendar DTE (days to expiration) from an ISO date string.
 * Returns 0 on same-day, negative for expired (clamped to 0 by callers).
 * Uses UTC to avoid timezone drift on expiration dates.
 */
function computeDTE(expiration: string): number {
  const exp  = Date.UTC(
    parseInt(expiration.slice(0, 4), 10),
    parseInt(expiration.slice(5, 7), 10) - 1,
    parseInt(expiration.slice(8, 10), 10),
  );
  const now  = Date.now();
  return Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
}

// ─── Mid-price ────────────────────────────────────────────────────────────────

function computeMidPrice(bid: number | null, ask: number | null): number | null {
  if (bid !== null && ask !== null && bid >= 0 && ask >= 0) {
    return (bid + ask) / 2;
  }
  return null;
}

// ─── Notional OI proxy ────────────────────────────────────────────────────────

/**
 * Proxy for position size. Real dealer GEX requires per-contract gamma × 100 × OI × spot.
 * We use OI × strike as a size-proxy that is bounded and direction-neutral.
 * We explicitly do NOT call this GEX to avoid false precision.
 */
function computeNotionalOIProxy(openInterest: number, strike: number): number {
  return openInterest * strike;
}

// ─── Main normalizer ──────────────────────────────────────────────────────────

/**
 * Normalizes a single raw option contract against the current spot price.
 * Returns null if the contract lacks required identity fields.
 */
export function normalizeContract(
  raw: RawOptionContract,
  spot: number,
): NormalizedOptionContract | null {
  if (!raw.strike || raw.strike <= 0) return null;
  if (!raw.expiration) return null;
  if (!raw.optionType) return null;
  if (spot <= 0) return null;

  const dte            = computeDTE(raw.expiration);
  const midPrice       = computeMidPrice(raw.bid, raw.ask);
  const distFromSpotPct = ((raw.strike - spot) / spot) * 100;
  const absDist        = Math.abs(distFromSpotPct);
  const isATM          = absDist <= ATM_BAND_PCT;
  const isNearMoney    = absDist <= NEAR_MONEY_PCT;
  const notionalOIProxy = computeNotionalOIProxy(raw.openInterest, raw.strike);

  return {
    symbol:          raw.symbol,
    underlying:      raw.underlying,
    strike:          raw.strike,
    expiration:      raw.expiration,
    optionType:      raw.optionType === 'C' ? 'call' : 'put',
    dte:             Math.max(0, dte),
    midPrice,
    distFromSpotPct,
    isATM,
    isNearMoney,
    notionalOIProxy,
    bid:             raw.bid,
    ask:             raw.ask,
    last:            raw.last,
    openInterest:    raw.openInterest,
    volume:          raw.volume,
    impliedVol:      raw.impliedVol,
    delta:           raw.delta,
    gamma:           raw.gamma !== null ? Math.abs(raw.gamma) : null, // always positive
    theta:           raw.theta,
    vega:            raw.vega,
  };
}

/**
 * Normalizes a full chain, filtering out:
 * - contracts with zero OI and zero volume (no market interest)
 * - contracts with DTE > maxDTE (too far out to be relevant for regime)
 * - expired contracts (DTE < 0 after clamp becomes 0 — included unless zero interest)
 *
 * Results are sorted strike ascending.
 */
export function normalizeChain(
  raw: RawOptionContract[],
  spot: number,
  maxDTE: number,
): NormalizedOptionContract[] {
  if (!raw.length || spot <= 0) return [];

  const result: NormalizedOptionContract[] = [];

  for (const contract of raw) {
    const norm = normalizeContract(contract, spot);
    if (!norm) continue;
    if (norm.dte > maxDTE) continue;
    if (norm.openInterest === 0 && norm.volume === 0) continue;
    result.push(norm);
  }

  return result.sort((a, b) => a.strike - b.strike);
}

/** Computes what fraction of contracts have actual gamma values (data quality signal). */
export function computeGammaDataCoverage(chain: NormalizedOptionContract[]): number {
  if (!chain.length) return 0;
  const withGamma = chain.filter(c => c.gamma !== null).length;
  return withGamma / chain.length;
}
